import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle2 } from "lucide-react";

const HeroSection = () => {
  const scrollToQuote = () => {
    const element = document.querySelector("#quote");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const benefits = [
    "Professional & Insured",
    "Sheffield Based",
    "Satisfaction Guaranteed",
  ];

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen pt-20 overflow-hidden hero-gradient"
    >
      {/* Background decoration */}
      <div className="absolute top-40 right-0 w-96 h-96 blue-glow rounded-full blur-3xl opacity-60" />
      <div className="absolute bottom-20 left-10 w-72 h-72 blue-glow rounded-full blur-3xl opacity-40" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-180px)]">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-[#3B82F6]/20 rounded-full px-4 py-2 shadow-sm mt-8 lg:mt-12">
              <Sparkles className="w-4 h-4 text-[#3B82F6]" />
              <span className="text-sm font-medium text-[#0F172A]">
                Sheffield's Certified Cleaning Specialist
              </span>
            </div>

            {/* Heading */}
            <div className="space-y-4">
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F172A] tracking-tight leading-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Professional Cleaning,{" "}
                <span className="text-[#3B82F6]">Exceptional Results</span>
              </h1>
              <p className="text-lg sm:text-xl text-[#64748B] max-w-xl leading-relaxed">
                Let an expert do it. From regular home cleaning to deep commercial
                services, we deliver spotless spaces across Sheffield.
              </p>
            </div>

            {/* Benefits */}
            <div className="flex flex-wrap gap-4">
              {benefits.map((benefit) => (
                <div
                  key={benefit}
                  className="flex items-center gap-2 text-[#0F172A]"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#3B82F6]" />
                  <span className="font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Button
                onClick={scrollToQuote}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 btn-shine"
                data-testid="hero-get-quote-btn"
              >
                Get Free Quote
              </Button>
              <Button
                onClick={() => {
                  const element = document.querySelector("#services");
                  element?.scrollIntoView({ behavior: "smooth" });
                }}
                variant="outline"
                className="border-[#3B82F6]/30 text-[#3B82F6] hover:bg-[#3B82F6]/5 px-8 py-6 text-lg rounded-full transition-all duration-300"
                data-testid="hero-services-btn"
              >
                Our Services
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="pt-8 border-t border-slate-200/50">
              <p className="text-sm text-[#64748B] mb-3">Why choose us?</p>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "Manrope, sans-serif" }}>Local</p>
                  <p className="text-xs text-[#64748B]">Sheffield Based</p>
                </div>
                <div className="w-px h-10 bg-slate-200" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "Manrope, sans-serif" }}>Insured</p>
                  <p className="text-xs text-[#64748B]">Fully Covered</p>
                </div>
                <div className="w-px h-10 bg-slate-200" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "Manrope, sans-serif" }}>Flexible</p>
                  <p className="text-xs text-[#64748B]">Your Schedule</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative animate-fade-in-up stagger-2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1649083048269-8bfb755e7b87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxicmlnaHQlMjBtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBkZXNpZ258ZW58MHx8fHwxNzcwNjg4OTQwfDA&ixlib=rb-4.1.0&q=85"
                alt="Clean modern living room"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>

            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 animate-float">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#3B82F6]/10 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-[#3B82F6]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]" style={{ fontFamily: "Manrope, sans-serif" }}>Sparkling Clean</p>
                  <p className="text-sm text-[#64748B]">Every Time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
